import { Bolt, DirectionsRun, MonitorHeart, PersonOff, Shield, Thunderstorm, WarningAmber } from "@mui/icons-material";
import { Box } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import { ResponseTile } from "~ui/molecules/ResponseTile/ResponseTile";

export const srpAvatarBg = {
  evacuate: "#1b5e20",
  hold: "#5e35b1",
  lockdown: "#b71c1c",
  secure: "#1565c0",
  shelter: "#e65100",
} as const;

/** I Love U Guys SRP assets — https://iloveuguys.org/The-Standard-Response-Protocol.html */
export const srpImage = {
  evacuate: "https://iloveuguys.org/logosandicons/evacuate.gif",
  hold: "https://iloveuguys.org/logosandicons/hold.gif",
  lockdown: "https://iloveuguys.org/logosandicons/lockdown.gif",
  secure: "https://iloveuguys.org/logosandicons/secure.gif",
  shelter: "https://iloveuguys.org/logosandicons/shelter.gif",
} as const;

/** Distinct card backgrounds per SRP action (different from each other and from generic critical/alert tiles). */
export const srpTileSx = {
  evacuate: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #66bb6a 0%, #2e7d32 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #43a047 0%, #1b5e20 100%)",
    color: "common.white",
  },
  hold: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #9575cd 0%, #5e35b1 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #7e57c2 0%, #4527a0 100%)",
    color: "common.white",
  },
  lockdown: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #ef5350 0%, #c62828 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #e53935 0%, #b71c1c 100%)",
    color: "common.white",
  },
  secure: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #42a5f5 0%, #1565c0 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #1e88e5 0%, #0d47a1 100%)",
    color: "common.white",
  },
  shelter: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #ffb74d 0%, #e65100 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #fb8c00 0%, #bf360c 100%)",
    color: "common.white",
  },
} as const satisfies Record<keyof typeof srpImage, SxProps<Theme>>;

export type CustomTileVariant = "interactive" | "disabled-drill" | "omit";

export interface EmergencyDispatchTileGridProps {
  disabled: boolean;
  /** Receives action ids matching `DISPATCH_EMERGENCY_KEYS` or `"custom"` when the custom tile is interactive. */
  onPress: (actionId: string) => void;
  /**
   * `interactive` — full visitor-management behavior (custom shows snackbar).
   * `disabled-drill` — same 6×6 layout as live; Custom is non-actionable (drills only use standard dispatch keys).
   * `omit` — hide Custom (uneven columns; rarely needed).
   */
  customTileVariant?: CustomTileVariant;
}

const gridCellSx = {
  minHeight: 0,
  minWidth: 0,
} as const;

/**
 * Two-column emergency action grid (same layout as visitor quick response and drill picker).
 * Uses CSS Grid so narrow viewports keep **rows of two** instead of stacking whole columns.
 */
export function EmergencyDispatchTileGrid({
  disabled,
  onPress,
  customTileVariant = "interactive",
}: EmergencyDispatchTileGridProps) {
  const customTile =
    customTileVariant === "omit" ? null : customTileVariant === "disabled-drill" ? (
      <ResponseTile
        disabled
        icon={<Shield />}
        kind="alert"
        label="Custom"
        sx={gridCellSx}
        title="Custom emergencies are not available as drills. Use a standard action or configure under Emergency Management."
        onPress={() => {}}
      />
    ) : (
      <ResponseTile
        disabled={disabled}
        icon={<Shield />}
        kind="alert"
        label="Custom"
        sx={gridCellSx}
        onPress={() => onPress("custom")}
      />
    );

  return (
    <Box
      sx={{
        display: "grid",
        flex: "1 1 auto",
        gap: 1,
        gridAutoRows: { md: "minmax(0, 1fr)", xs: "auto" },
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        minHeight: 0,
      }}
    >
      <ResponseTile
        disabled={disabled}
        icon={<WarningAmber />}
        kind="critical"
        label="Active Shooter"
        sx={gridCellSx}
        onPress={() => onPress("active-shooter")}
      />
      <ResponseTile
        disabled={disabled}
        icon={<DirectionsRun />}
        kind="critical"
        label="Emergency Evacuation"
        sx={gridCellSx}
        onPress={() => onPress("emergency-evacuation")}
      />
      <ResponseTile
        avatarBg={srpAvatarBg.hold}
        disabled={disabled}
        imageAlt="SRP Hold"
        imageSrc={srpImage.hold}
        kind="alert"
        label="SRP — Hold"
        sx={{ ...srpTileSx.hold, ...gridCellSx }}
        onPress={() => onPress("srp-hold")}
      />
      <ResponseTile
        disabled={disabled}
        icon={<MonitorHeart />}
        kind="alert"
        label="Medical"
        sx={gridCellSx}
        onPress={() => onPress("medical")}
      />
      <ResponseTile
        avatarBg={srpAvatarBg.secure}
        disabled={disabled}
        imageAlt="SRP Secure"
        imageSrc={srpImage.secure}
        kind="alert"
        label="SRP — Secure"
        sx={{ ...srpTileSx.secure, ...gridCellSx }}
        onPress={() => onPress("srp-secure")}
      />
      <ResponseTile
        disabled={disabled}
        icon={<Thunderstorm />}
        kind="alert"
        label="Weather"
        sx={gridCellSx}
        onPress={() => onPress("weather")}
      />
      <ResponseTile
        avatarBg={srpAvatarBg.lockdown}
        disabled={disabled}
        imageAlt="SRP Lockdown"
        imageSrc={srpImage.lockdown}
        kind="alert"
        label="SRP — Lockdown"
        sx={{ ...srpTileSx.lockdown, ...gridCellSx }}
        onPress={() => onPress("srp-lockdown")}
      />
      <ResponseTile
        disabled={disabled}
        icon={<PersonOff />}
        kind="alert"
        label="Stranger"
        sx={gridCellSx}
        onPress={() => onPress("stranger")}
      />
      <ResponseTile
        avatarBg={srpAvatarBg.evacuate}
        disabled={disabled}
        imageAlt="SRP Evacuate"
        imageSrc={srpImage.evacuate}
        kind="alert"
        label="SRP — Evacuate"
        sx={{ ...srpTileSx.evacuate, ...gridCellSx }}
        onPress={() => onPress("srp-evacuate")}
      />
      <ResponseTile
        disabled={disabled}
        icon={<Bolt />}
        kind="alert"
        label="Fight"
        sx={gridCellSx}
        onPress={() => onPress("fight")}
      />
      <ResponseTile
        avatarBg={srpAvatarBg.shelter}
        disabled={disabled}
        imageAlt="SRP Shelter"
        imageSrc={srpImage.shelter}
        kind="alert"
        label="SRP — Shelter"
        sx={{
          ...srpTileSx.shelter,
          ...gridCellSx,
          ...(customTileVariant === "omit" ? { gridColumn: "1 / -1" } : {}),
        }}
        onPress={() => onPress("srp-shelter")}
      />
      {customTile}
    </Box>
  );
}
